import { useEffect } from 'react';

import { isUUIDV4 } from '@aline/common';
import { Apollo } from 'src/gql/generated';

/**
 * The part of a report column option this reads. Structural, so the
 * `reportColumnOptions` query result is handed over as-is.
 */
type ReportColumnOptionLike =
  | ({ __typename: 'ExtractionReportColumn' } & Pick<Apollo.ExtractionReportColumn, 'questionId'>)
  | { __typename: 'FileFieldColumn' };

/**
 * The grouping model without any level keyed on an extraction question the org
 * no longer has. Built-in columns are kept as they are; a question level is
 * identified by its UUID field, as on the server.
 */
export function getGroupingWithoutDeletedQuestions(
  rowGroupingModel: readonly string[],
  reportColumnOptions: readonly ReportColumnOptionLike[]
): string[] {
  const questionIds = new Set(
    reportColumnOptions.flatMap(option =>
      option.__typename === 'ExtractionReportColumn' ? [option.questionId] : []
    )
  );
  return rowGroupingModel.filter(field => !isUUIDV4(field) || questionIds.has(field));
}

/**
 * Drops a grouping level whose extraction question has been deleted and keeps
 * the rest, so the grid regroups by what is left, or goes flat, instead of
 * asking the server for a grouping it will refuse on every load and every
 * expand.
 *
 * `reportColumnOptions` is the org's question list: the set the server
 * validates a grouping against, and the one `useDeleteExtractionQuestion`
 * trims in the cache, so a deletion from any report's column menu reaches
 * every mounted grid at once. Nothing is dropped until the list has loaded,
 * since an absent list says nothing about a question.
 *
 * The setter is only called when a level actually goes. React.Activity tears
 * this effect down when the tab hides and runs it again on show, and a fresh
 * array for an unchanged model reads as a grouping change to
 * `useReportDataSource`, which clears the data-source cache and refetches.
 */
export function useDropDeletedQuestionsFromGrouping({
  rowGroupingModel,
  setRowGroupingModel,
}: {
  rowGroupingModel: string[];
  setRowGroupingModel: (model: string[]) => void;
}): void {
  const { data } = Apollo.useGetReportColumnOptionsQuery();
  const reportColumnOptions = data?.reportColumnOptions;

  useEffect(() => {
    if (!reportColumnOptions) return;
    const kept = getGroupingWithoutDeletedQuestions(rowGroupingModel, reportColumnOptions);
    if (kept.length !== rowGroupingModel.length) setRowGroupingModel(kept);
  }, [reportColumnOptions, rowGroupingModel, setRowGroupingModel]);
}
